import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';

const AffiliateSignupPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1);
  
  // Form fields
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [website, setWebsite] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('later');
  const [paypalEmail, setPaypalEmail] = useState('');
  const [bankName, setBankName] = useState('');
  const [bankIban, setBankIban] = useState('');
  const [bic, setBic] = useState('');
  const [accountHolder, setAccountHolder] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);

  const handleNextStep = (e) => {
    e.preventDefault();
    
    if (step === 1) {
      // Validate first step
      if (!email || !password || !confirmPassword) {
        setError(t('affiliate.signup.error.allFieldsRequired'));
        return;
      }
      
      if (password !== confirmPassword) {
        setError(t('affiliate.signup.error.passwordMismatch'));
        return;
      }
      
      if (password.length < 8) {
        setError(t('affiliate.signup.error.passwordTooShort'));
        return;
      }
    }
    
    setError(null);
    setStep(step + 1);
  };

  const handlePrevStep = () => {
    setError(null);
    setStep(step - 1);
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    
    // Validate final step
    if (!firstName || !lastName || !paymentMethod) {
      setError(t('affiliate.signup.error.allFieldsRequired'));
      return;
    }
    
    if (paymentMethod === 'paypal' && !paypalEmail) {
      setError(t('affiliate.signup.error.paypalEmailRequired'));
      return;
    }
    
    if (paymentMethod === 'bank_transfer' && (!bankName || !bankIban || !bic || !accountHolder)) {
      setError(t('affiliate.signup.error.bankDetailsRequired'));
      return;
    }
    
    // No validation needed for 'later' option
    
    if (!termsAccepted) {
      setError(t('affiliate.signup.error.termsRequired'));
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Step 1: Create Firebase Auth user
      const auth = getAuth();
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Step 2: Create affiliate profile
      const functions = getFunctions();
      const createAffiliate = httpsCallable(functions, 'createAffiliate');
      
      // Prepare payment details based on selected method
      let paymentDetails = {};
      if (paymentMethod === 'paypal') {
        paymentDetails = { paypalEmail };
      } else if (paymentMethod === 'bank_transfer') {
        paymentDetails = { bankName, bankIban, bic, accountHolder };
      } else if (paymentMethod === 'later') {
        paymentDetails = { configured: false };
      }
      
      const result = await createAffiliate({
        firstName,
        lastName,
        email,
        phone: phone || null,
        companyName: companyName || null,
        website: website || null,
        paymentMethod,
        paymentDetails
      });
      
      console.log('Affiliate created:', result.data);
      
      // Navigate to dashboard on success
      navigate('/affiliate/dashboard');
    } catch (error) {
      console.error('Signup error:', error);
      setError(error.message || t('affiliate.signup.error.generic'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PageContainer>
      <SignupContainer
        as={motion.div}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <SignupHeader>
          <h1>{t('affiliate.signup.title')}</h1>
          <p>{t('affiliate.signup.subtitle')}</p>
        </SignupHeader>
        
        <StepIndicator>
          <StepDot active={step >= 1} completed={step > 1}>1</StepDot>
          <StepLine completed={step > 1} />
          <StepDot active={step >= 2} completed={step > 2}>2</StepDot>
          <StepLine completed={step > 2} />
          <StepDot active={step >= 3}>3</StepDot>
        </StepIndicator>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
        
        {step === 1 && (
          <FormContainer onSubmit={handleNextStep}>
            <StepTitle>{t('affiliate.signup.step1.title')}</StepTitle>
            <FormGroup>
              <FormLabel htmlFor="email">{t('affiliate.signup.step1.email')}</FormLabel>
              <FormInput
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </FormGroup>
            
            <FormGroup>
              <FormLabel htmlFor="password">{t('affiliate.signup.step1.password')}</FormLabel>
              <FormInput
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </FormGroup>
            
            <FormGroup>
              <FormLabel htmlFor="confirmPassword">
                {t('affiliate.signup.step1.confirmPassword')}
              </FormLabel>
              <FormInput
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </FormGroup>
            
            <FormButton type="submit">
              {t('affiliate.signup.step1.next')}
            </FormButton>
            
            <LoginLink>
              {t('affiliate.signup.alreadyHaveAccount')}
              <Link to="/affiliate/login"> {t('affiliate.signup.login')}</Link>
            </LoginLink>
          </FormContainer>
        )}
        
        {step === 2 && (
          <FormContainer onSubmit={handleNextStep}>
            <StepTitle>{t('affiliate.signup.step2.title')}</StepTitle>
            <FormGroup>
              <FormLabel htmlFor="firstName">{t('affiliate.signup.step2.firstName')}</FormLabel>
              <FormInput
                type="text"
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
              />
            </FormGroup>
            
            <FormGroup>
              <FormLabel htmlFor="lastName">{t('affiliate.signup.step2.lastName')}</FormLabel>
              <FormInput
                type="text"
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
              />
            </FormGroup>
            
            <FormGroup>
              <FormLabel htmlFor="phone">{t('affiliate.signup.step2.phone')}</FormLabel>
              <FormInput
                type="tel"
                id="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </FormGroup>
            
            <FormGroup>
              <FormLabel htmlFor="companyName">{t('affiliate.signup.step2.companyName')}</FormLabel>
              <FormInput
                type="text"
                id="companyName"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
              />
            </FormGroup>
            
            <FormGroup>
              <FormLabel htmlFor="website">{t('affiliate.signup.step2.website')}</FormLabel>
              <FormInput
                type="url"
                id="website"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                placeholder="https://"
              />
            </FormGroup>
            
            <FormButtonGroup>
              <FormButton type="button" secondary onClick={handlePrevStep}>
                {t('affiliate.signup.step2.back')}
              </FormButton>
              <FormButton type="submit">
                {t('affiliate.signup.step2.next')}
              </FormButton>
            </FormButtonGroup>
          </FormContainer>
        )}
        
        {step === 3 && (
          <FormContainer onSubmit={handleSignup}>
            <StepTitle>{t('affiliate.signup.step3.title')}</StepTitle>
            <FormGroup>
              <FormLabel>{t('affiliate.signup.step3.paymentMethod')}</FormLabel>
              <RadioGroup>
                <RadioOption>
                  <RadioInput
                    type="radio"
                    id="later"
                    name="paymentMethod"
                    value="later"
                    checked={paymentMethod === 'later'}
                    onChange={() => setPaymentMethod('later')}
                  />
                  <RadioLabel htmlFor="later">
                    {t('affiliate.signup.step3.setupLater')}
                  </RadioLabel>
                </RadioOption>
                
                <RadioOption>
                  <RadioInput
                    type="radio"
                    id="paypal"
                    name="paymentMethod"
                    value="paypal"
                    checked={paymentMethod === 'paypal'}
                    onChange={() => setPaymentMethod('paypal')}
                  />
                  <RadioLabel htmlFor="paypal">PayPal</RadioLabel>
                </RadioOption>
                
                <RadioOption>
                  <RadioInput
                    type="radio"
                    id="bank_transfer"
                    name="paymentMethod"
                    value="bank_transfer"
                    checked={paymentMethod === 'bank_transfer'}
                    onChange={() => setPaymentMethod('bank_transfer')}
                  />
                  <RadioLabel htmlFor="bank_transfer">
                    {t('affiliate.signup.step3.bankTransfer')}
                  </RadioLabel>
                </RadioOption>
              </RadioGroup>
            </FormGroup>
            
            {paymentMethod === 'paypal' && (
              <FormGroup>
                <FormLabel htmlFor="paypalEmail">
                  {t('affiliate.signup.step3.paypalEmail')}
                </FormLabel>
                <FormInput
                  type="email"
                  id="paypalEmail"
                  value={paypalEmail}
                  onChange={(e) => setPaypalEmail(e.target.value)}
                  required
                />
              </FormGroup>
            )}
            
            {paymentMethod === 'bank_transfer' && (
              <>
                <FormGroup>
                  <FormLabel htmlFor="accountHolder">
                    {t('affiliate.signup.step3.accountHolder')}
                  </FormLabel>
                  <FormInput
                    type="text"
                    id="accountHolder"
                    value={accountHolder}
                    onChange={(e) => setAccountHolder(e.target.value)}
                    required
                  />
                </FormGroup>
                
                <FormGroup>
                  <FormLabel htmlFor="bankName">
                    {t('affiliate.signup.step3.bankName')}
                  </FormLabel>
                  <FormInput
                    type="text"
                    id="bankName"
                    value={bankName}
                    onChange={(e) => setBankName(e.target.value)}
                    required
                  />
                </FormGroup>
                
                <FormGroup>
                  <FormLabel htmlFor="bankIban">
                    {t('affiliate.signup.step3.bankIban')}
                  </FormLabel>
                  <FormInput
                    type="text"
                    id="bankIban"
                    value={bankIban}
                    onChange={(e) => setBankIban(e.target.value)}
                    required
                  />
                </FormGroup>
                
                <FormGroup>
                  <FormLabel htmlFor="bic">
                    {t('affiliate.signup.step3.bic')}
                  </FormLabel>
                  <FormInput
                    type="text"
                    id="bic"
                    value={bic}
                    onChange={(e) => setBic(e.target.value)}
                    required
                  />
                </FormGroup>
              </>
            )}
            
            <FormGroup>
              <CheckboxContainer>
                <CheckboxInput
                  type="checkbox"
                  id="termsAccepted"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  required
                />
                <CheckboxLabel htmlFor="termsAccepted">
                  {t('affiliate.signup.step3.termsAccepted')}{' '}
                  <Link to="/affiliate/terms" target="_blank">
                    {t('affiliate.signup.step3.termsLink')}
                  </Link>
                </CheckboxLabel>
              </CheckboxContainer>
            </FormGroup>
            
            <FormButtonGroup>
              <FormButton type="button" secondary onClick={handlePrevStep}>
                {t('affiliate.signup.step3.back')}
              </FormButton>
              <FormButton type="submit" disabled={isLoading}>
                {isLoading
                  ? t('affiliate.signup.step3.processing')
                  : t('affiliate.signup.step3.createAccount')}
              </FormButton>
            </FormButtonGroup>
          </FormContainer>
        )}
      </SignupContainer>
    </PageContainer>
  );
};

// Styled Components
const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  background-color: #f8f9fa;
`;

const SignupContainer = styled.div`
  max-width: 600px;
  width: 100%;
  background: white;
  border-radius: 10px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
  padding: 40px;
  
  @media (max-width: 600px) {
    padding: 30px 20px;
  }
`;

const SignupHeader = styled.div`
  text-align: center;
  margin-bottom: 30px;
  
  h1 {
    margin-bottom: 10px;
    color: #333;
  }
  
  p {
    color: #666;
  }
`;

const StepIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 30px;
`;

const StepDot = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: bold;
  background-color: ${props => 
    props.active 
      ? 'linear-gradient(135deg, #6e8efb 0%, #a777e3 100%)'
      : '#e0e0e0'
  };
  background: ${props => 
    props.active 
      ? 'linear-gradient(135deg, #6e8efb 0%, #a777e3 100%)'
      : '#e0e0e0'
  };
  color: ${props => props.active ? 'white' : '#666'};
  border: 2px solid ${props => 
    props.completed 
      ? '#a777e3'
      : props.active 
        ? '#6e8efb'
        : '#e0e0e0'
  };
`;

const StepLine = styled.div`
  flex: 1;
  height: 2px;
  background-color: ${props => props.completed ? '#a777e3' : '#e0e0e0'};
  margin: 0 10px;
`;

const StepTitle = styled.h2`
  font-size: 1.5rem;
  margin-bottom: 20px;
  color: #333;
  text-align: center;
`;

const ErrorMessage = styled.div`
  background-color: #ffe0e0;
  color: #d32f2f;
  padding: 10px 15px;
  border-radius: 5px;
  margin-bottom: 20px;
  font-size: 0.9rem;
`;

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
  color: #333;
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
  color: #333 ;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 1rem;
  transition: border-color 0.3s;
  
  &:focus {
    border-color: #6e8efb;
    outline: none;
  }
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const RadioOption = styled.div`
  display: flex;
  align-items: center;
`;

const RadioInput = styled.input`
  margin-right: 10px;
  
  &:checked {
    accent-color: #6e8efb;
  }
`;

const RadioLabel = styled.label`
  font-weight: normal;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: flex-start;
`;

const CheckboxInput = styled.input`
  margin-right: 10px;
  margin-top: 3px;
  
  &:checked {
    accent-color: #6e8efb;
  }
`;

const CheckboxLabel = styled.label`
  font-weight: normal;
  font-size: 0.9rem;
  line-height: 1.4;
  
  a {
    color: #6e8efb;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const FormButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 10px;
`;

const FormButton = styled.button`
  flex: ${props => props.secondary ? '0 0 auto' : '1'};
  background: ${props => props.secondary 
    ? 'transparent' 
    : 'linear-gradient(135deg, #6e8efb 0%, #a777e3 100%)'
  };
  color: ${props => props.secondary ? '#6e8efb' : 'white'};
  border: ${props => props.secondary ? '1px solid #6e8efb' : 'none'};
  padding: 12px 20px;
  border-radius: 5px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.secondary 
      ? 'none' 
      : '0 5px 15px rgba(110, 142, 251, 0.2)'
    };
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const LoginLink = styled.p`
  text-align: center;
  margin-top: 20px;
  font-size: 0.9rem;
  color: #666;
  
  a {
    color: #6e8efb;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

export default AffiliateSignupPage;